/**
 * Offline Face Detection using ONNX models
 * Provides fallback face detection when server is unavailable
 */

import * as ort from 'onnxruntime-web';

let yolov8Model = null;
let deepsortTracker = new Map(); // Simple in-memory tracker

const CONFIDENCE_THRESHOLD = 0.5;

/**
 * Load ONNX face detection model
 */
export async function loadOfflineFaceDetectionModel() {
  try {
    if (yolov8Model) return yolov8Model;

    // Use the ONNX model from public/models
    const session = await ort.InferenceSession.create(
      '/models/yolov8n-face.onnx'
    );
    yolov8Model = session;
    console.log('✅ Offline face detection model loaded');
    return session;
  } catch (error) {
    console.error('❌ Failed to load offline detection model:', error);
    return null;
  }
}

/**
 * Detect faces in image (offline version using ONNX)
 * @param {string} imageBase64 - Base64 encoded image
 * @returns {Promise<{tracks: Array, offline: boolean}>}
 */
export async function detectAndTrackOffline(imageBase64) {
  try {
    const model = await loadOfflineFaceDetectionModel();
    if (!model) {
      throw new Error('Face detection model not loaded');
    }

    // Convert base64 to canvas
    const img = new Image();
    img.src = imageBase64;

    return new Promise((resolve) => {
      img.onload = async () => {
        try {
          // Prepare input tensor
          const canvas = document.createElement('canvas');
          canvas.width = 640;
          canvas.height = 640;
          const ctx = canvas.getContext('2d');

          // Draw image with aspect ratio preservation
          const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
          const x = (canvas.width - img.width * scale) / 2;
          const y = (canvas.height - img.height * scale) / 2;
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

          // Convert to tensor
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = new Float32Array(imageData.data.length);
          for (let i = 0; i < imageData.data.length; i += 4) {
            data[i] = imageData.data[i] / 255; // R
            data[i + 1] = imageData.data[i + 1] / 255; // G
            data[i + 2] = imageData.data[i + 2] / 255; // B
          }

          const tensor = new ort.Tensor('float32', data, [1, 4, 640, 640]);

          // Run inference
          const feeds = { images: tensor };
          const results = await model.run(feeds);

          // Parse detections
          const detections = parseYoloOutput(results, img.width, img.height);

          // Update tracker
          const tracks = updateSimpleTracker(detections);

          resolve({
            tracks,
            offline: true,
            message: 'Offline detection (client-side)',
            detections: detections.length,
          });
        } catch (error) {
          console.error('Offline detection error:', error);
          resolve({
            tracks: [],
            offline: true,
            error: error.message,
          });
        }
      };

      img.onerror = () => {
        resolve({
          tracks: [],
          offline: true,
          error: 'Failed to load image',
        });
      };
    });
  } catch (error) {
    console.error('❌ Offline face detection failed:', error);
    return {
      tracks: [],
      offline: true,
      error: error.message,
    };
  }
}

/**
 * Parse YOLO output to detections
 */
function parseYoloOutput(results, imgWidth, imgHeight) {
  const detections = [];
  // Assuming YOLO output format: [batch, 25200, 6] (x, y, w, h, conf, class)
  
  try {
    const output = results.output0.data || results.output?.data || [];
    const stride = 6; // x, y, w, h, conf, class

    for (let i = 0; i < output.length; i += stride) {
      const conf = output[i + 4];

      if (conf >= CONFIDENCE_THRESHOLD) {
        const [x, y, w, h] = [output[i], output[i + 1], output[i + 2], output[i + 3]];

        detections.push({
          bbox: {
            x1: Math.max(0, (x - w / 2) * imgWidth / 640),
            y1: Math.max(0, (y - h / 2) * imgHeight / 640),
            x2: Math.min(imgWidth, (x + w / 2) * imgWidth / 640),
            y2: Math.min(imgHeight, (y + h / 2) * imgHeight / 640),
          },
          confidence: conf,
          class: Math.round(output[i + 5]),
        });
      }
    }
  } catch (error) {
    console.warn('Error parsing YOLO output:', error);
  }

  return detections;
}

/**
 * Simple DeepSORT-like tracker for offline mode
 */
function updateSimpleTracker(detections) {
  const tracks = [];
  const maxDistance = 50; // pixels
  const trackedIds = new Set();

  // Try to match new detections with existing tracks
  for (const detection of detections) {
    let matched = false;
    let closestTrackId = null;
    let closestDistance = maxDistance;

    for (const [trackId, lastBbox] of deepsortTracker.entries()) {
      const distance = calculateIoU(detection.bbox, lastBbox);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestTrackId = trackId;
        matched = true;
      }
    }

    if (matched && closestTrackId !== null) {
      trackedIds.add(closestTrackId);
      deepsortTracker.set(closestTrackId, detection.bbox);
      tracks.push({
        track_id: closestTrackId,
        bbox: detection.bbox,
        confidence: detection.confidence,
      });
    } else {
      // New track
      const newId = Math.max(...Array.from(deepsortTracker.keys()), 0) + 1;
      deepsortTracker.set(newId, detection.bbox);
      tracks.push({
        track_id: newId,
        bbox: detection.bbox,
        confidence: detection.confidence,
      });
    }
  }

  // Clean up old tracks not matched in this frame
  for (const trackId of deepsortTracker.keys()) {
    if (!trackedIds.has(trackId)) {
      deepsortTracker.delete(trackId);
    }
  }

  return tracks;
}

/**
 * Calculate Intersection over Union (IoU) between two bounding boxes
 */
function calculateIoU(box1, box2) {
  const intersection =
    Math.max(0, Math.min(box1.x2, box2.x2) - Math.max(box1.x1, box2.x1)) *
    Math.max(0, Math.min(box1.y2, box2.y2) - Math.max(box1.y1, box2.y1));

  const area1 = (box1.x2 - box1.x1) * (box1.y2 - box1.y1);
  const area2 = (box2.x2 - box2.x1) * (box2.y2 - box2.y1);
  const union = area1 + area2 - intersection;

  return union === 0 ? 0 : 1 - intersection / union;
}

/**
 * Reset tracker state
 */
export function resetTracker() {
  deepsortTracker.clear();
}
